import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, Checkbox, CircularProgress,
  Container, FormControlLabel, Grid, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import {
  useApplyFunnelCorrectionMutation,
  useGetFunnelCorrectionPreviewQuery,
} from '../slices/projectsApiSlice';

const localDateTime = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return '';
  const two = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${two(date.getMonth() + 1)}-${two(date.getDate())}T${two(date.getHours())}:${two(date.getMinutes())}:${two(date.getSeconds())}`;
};
const toIso = (value) => value ? new Date(value).toISOString() : null;
const stateTitle = { '': 'Не начат', start: 'Начат', done: 'Завершён' };
const displayDate = (value) => value ? new Date(value).toLocaleString('ru-RU') : '—';

export default function FunnelCorrectionScreen() {
  const { section, id } = useParams();
  const navigate = useNavigate();
  const [toType, setToType] = useState('');
  const [rows, setRows] = useState([]);
  const [enteredAt, setEnteredAt] = useState('');
  const [reason, setReason] = useState('');
  const [review, setReview] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [message, setMessage] = useState('');
  const { data: base, isLoading: loadingBase, error: baseError } =
    useGetFunnelCorrectionPreviewQuery({ projectId: id });
  const { data: preview, isLoading: loadingPreview, error: previewError } =
    useGetFunnelCorrectionPreviewQuery({ projectId: id, toType }, { skip: !toType });
  const [applyCorrection, { isLoading: saving }] = useApplyFunnelCorrectionMutation();

  useEffect(() => {
    if (!preview || preview.targetType?.id !== toType) return;
    const sourceById = new Map(preview.sourceStages.map((stage) => [stage.id, stage]));
    setRows(preview.targetStages.map((stage) => {
      const source = sourceById.get(stage.suggestedSourceId);
      const state = source?.state || '';
      return {
        stageId: stage.stageId, sourceStageId: source?.id || '',
        state, started: state === '' ? '' : localDateTime(source?.started),
        finished: state === 'done' ? localDateTime(source?.finished) : '',
      };
    }));
    setEnteredAt(localDateTime(preview.project.createdAt));
    setReview(false);
    setConfirmed(false);
  }, [preview, toType]);

  const changeRow = (stageId, changes) => {
    setRows((current) => current.map((row) => row.stageId === stageId ? { ...row, ...changes } : row));
    setReview(false);
    setConfirmed(false);
  };
  const chooseSource = (stageId, sourceStageId) => {
    const source = preview.sourceStages.find((stage) => stage.id === sourceStageId);
    const state = source?.state || '';
    changeRow(stageId, {
      sourceStageId, state,
      started: state === '' ? '' : localDateTime(source?.started),
      finished: state === 'done' ? localDateTime(source?.finished) : '',
    });
  };
  const chooseState = (stageId, state) => changeRow(stageId, {
    state,
    started: state === '' ? '' : rows.find((row) => row.stageId === stageId)?.started || '',
    finished: state === 'done' ? rows.find((row) => row.stageId === stageId)?.finished || '' : '',
  });
  const validate = () => {
    if (!enteredAt) return 'Укажите историческую дату входа в новую воронку.';
    if (reason.trim().length < 5) return 'Укажите причину исправления.';
    if (rows.length !== preview.targetStages.length) return 'Дождитесь загрузки всех этапов.';
    for (const row of rows) {
      const title = preview.targetStages.find((stage) => stage.stageId === row.stageId)?.title;
      if (row.state === 'start' && !row.started) return `Укажите дату начала: ${title}.`;
      if (row.state === 'done' && !row.finished) return `Укажите дату завершения: ${title}.`;
      if (row.started && row.finished && new Date(row.started) > new Date(row.finished)) return `Даты перепутаны: ${title}.`;
    }
    return '';
  };
  const showReview = () => {
    const error = validate();
    if (error) { setMessage(error); return; }
    setMessage('');
    setReview(true);
  };
  const save = async () => {
    if (!confirmed || !preview || saving) return;
    try {
      await applyCorrection({ projectId: id, targetTypeId: toType, version: preview.version,
        reason: reason.trim(), enteredAt: toIso(enteredAt), confirmed,
        rows: rows.map((row) => ({ stageId: row.stageId,
          sourceStageId: row.sourceStageId || null, state: row.state,
          started: toIso(row.started), finished: toIso(row.finished),
        })),
      }).unwrap();
      navigate(`/crm/${section}/${id}`);
    } catch (error) {
      setMessage(error?.data?.message || error?.error || 'Не удалось перенести проект. Обновите форму.');
      setReview(false);
      setConfirmed(false);
    }
  };

  return (
    <Container maxWidth='md' sx={{ py: 2 }}>
      <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
        <Typography variant='h5'>Исправление воронки проекта</Typography>
        <Button onClick={() => navigate(`/crm/${section}/${id}`)}>Закрыть</Button>
      </Stack>
      {loadingBase && <CircularProgress />}
      {baseError && <Alert severity='error'>{baseError?.data?.message || 'Не удалось загрузить проект'}</Alert>}
      {base && <Typography sx={{ mb: 2 }}><strong>{base.project.title}</strong> · Сейчас: {base.project.typeTitle}</Typography>}
      {message && <Alert severity='error' sx={{ mb: 2 }}>{message}</Alert>}
      {base && <TextField select fullWidth size='small' label='Новая воронка' value={toType}
        onChange={(event) => { setToType(event.target.value); setRows([]); setReview(false); setConfirmed(false); setMessage(''); }}
        sx={{ mb: 2 }}>
        {base.types.map((type) => <MenuItem key={type.id} value={type.id}>{type.title}</MenuItem>)}
      </TextField>}
      {loadingPreview && <CircularProgress />}
      {previewError && <Alert severity='error'>{previewError?.data?.message || 'Не удалось загрузить этапы'}</Alert>}
      {preview && preview.targetType.id === toType && <>
        {preview.existingTargetCount > 0 && <Alert severity='error' sx={{ mb: 2 }}>
          У проекта уже есть этапы этой воронки. Автоматическое исправление заблокировано: сначала нужно проверить их историю.
        </Alert>}
        {(preview.warnings.duplicateShortNames.length > 0 || preview.warnings.duplicateOrders.length > 0) &&
          <Alert severity='warning' sx={{ mb: 2 }}>
            Проверьте настройки новой воронки: совпадающие сокращения этапов — {preview.warnings.duplicateShortNames.join(', ') || 'нет'};
            совпадающий порядок — {preview.warnings.duplicateOrders.join(', ') || 'нет'}.
          </Alert>}
        <Typography variant='h6' sx={{ mb: 1 }}>Даты и этапы прежней воронки</Typography>
        <Stack spacing={0.5} sx={{ mb: 3 }}>
          {preview.sourceStages.map((stage) => <Typography key={stage.id} variant='body2'>
            {stage.title} · {stateTitle[stage.state] || stage.state} · начало {displayDate(stage.started)} · завершение {displayDate(stage.finished)} · изменение {displayDate(stage.updatedAt)}
          </Typography>)}
        </Stack>
        <Alert severity='info' sx={{ mb: 2 }}>
          Совпадающие этапы предложены автоматически. Проверьте каждое сопоставление и дату. Старые записи сохранятся в журнале исправления; отчёты будут использовать подтверждённые исторические даты.
        </Alert>
        <Typography variant='h6' sx={{ mb: 1 }}>Этапы новой воронки</Typography>
        <Stack spacing={1.5} sx={{ mb: 3 }}>
          {preview.targetStages.map((stage) => {
            const row = rows.find((item) => item.stageId === stage.stageId);
            if (!row) return null;
            return <Card key={stage.stageId} variant='outlined'><CardContent sx={{ '&:last-child': { pb: 2 } }}>
              <Typography sx={{ mb: 1 }}><strong>{stage.title}</strong> {stage.shortName && `(${stage.shortName})`}</Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} md={4}>
                  <TextField select fullWidth size='small' label='Взять данные из старого этапа'
                    value={row.sourceStageId} onChange={(event) => chooseSource(stage.stageId, event.target.value)}>
                    <MenuItem value=''>Нет соответствия — указать вручную</MenuItem>
                    {preview.sourceStages.map((source) => <MenuItem key={source.id} value={source.id}>
                      {source.order}. {source.title} · {stateTitle[source.state] || source.state}
                    </MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField select fullWidth size='small' label='Состояние' value={row.state}
                    onChange={(event) => chooseState(stage.stageId, event.target.value)}>
                    {Object.entries(stateTitle).map(([value, title]) => <MenuItem key={value} value={value}>{title}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField fullWidth size='small' label='Начало' type='datetime-local' inputProps={{ step: 1 }} InputLabelProps={{ shrink: true }}
                    value={row.started} disabled={row.state === ''}
                    onChange={(event) => changeRow(stage.stageId, { started: event.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField fullWidth size='small' label='Завершение' type='datetime-local' inputProps={{ step: 1 }} InputLabelProps={{ shrink: true }}
                    value={row.finished} disabled={row.state !== 'done'}
                    onChange={(event) => changeRow(stage.stageId, { finished: event.target.value })} />
                </Grid>
              </Grid>
            </CardContent></Card>;
          })}
        </Stack>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={5}><TextField fullWidth size='small' label='Историческая дата входа в новую воронку'
            type='datetime-local' inputProps={{ step: 1 }} InputLabelProps={{ shrink: true }} value={enteredAt}
            onChange={(event) => { setEnteredAt(event.target.value); setReview(false); setConfirmed(false); }} /></Grid>
          <Grid item xs={12} sm={7}><TextField fullWidth size='small' label='Причина исправления'
            value={reason} onChange={(event) => { setReason(event.target.value); setReview(false); setConfirmed(false); }} /></Grid>
        </Grid>
        {!review ? <Button variant='contained' disabled={preview.existingTargetCount > 0 || rows.length !== preview.targetStages.length}
          onClick={showReview}>Проверить перенос</Button> : <>
          <Typography variant='h6' sx={{ mb: 1 }}>Проверьте итог перед сохранением</Typography>
          <Typography sx={{ mb: 1 }}>{preview.project.typeTitle} → {preview.targetType.title}; дата входа: {displayDate(toIso(enteredAt))}</Typography>
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            {rows.map((row) => <Typography key={row.stageId} variant='body2'>
              {preview.targetStages.find((stage) => stage.stageId === row.stageId)?.title} ← {preview.sourceStages.find((stage) => stage.id === row.sourceStageId)?.title || 'без старого этапа'}:
              {' '}{stateTitle[row.state]}; начало {displayDate(toIso(row.started))}; завершение {displayDate(toIso(row.finished))}
            </Typography>)}
          </Stack>
          <Alert severity='info' sx={{ mb: 1 }}>
            Исходные записи, которые останутся только в архиве и перестанут учитываться в отчётах:
            {' '}{preview.sourceStages.filter((source) => !rows.some((row) => row.sourceStageId === source.id && row.stageId === source.stageId))
              .map((source) => source.title).join(', ') || 'нет'}.
          </Alert>
          {rows.some((row, index) => row.sourceStageId && rows.findIndex((other) => other.sourceStageId === row.sourceStageId) !== index) &&
            <Alert severity='warning' sx={{ mb: 1 }}>Один исходный этап выбран для нескольких новых. Проверьте даты каждого из них.</Alert>}
          <FormControlLabel control={<Checkbox checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />}
            label='Я проверил сопоставление всех этапов и отвечаю за указанные исторические даты' />
          <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
            <Button onClick={() => { setReview(false); setConfirmed(false); }}>Изменить</Button>
            <Button variant='contained' color='warning' disabled={!confirmed || saving || preview.existingTargetCount > 0}
              onClick={save}>{saving ? 'Сохраняем…' : 'Применить исправление'}</Button>
          </Box>
        </>}
      </>}
    </Container>
  );
}
